class GivebackComponent extends HTMLElement {
  #tabs = [];
  #panels = [];
  #defaultTab = 'community';

  connectedCallback() {
    this.#tabs = [...this.querySelectorAll('[data-giveback-tab]')];
    this.#panels = [...this.querySelectorAll('[data-giveback-panel]')];
    this.#defaultTab = this.dataset.defaultTab || 'community';

    if (!this.#tabs.length || !this.#panels.length) return;

    for (const tab of this.#tabs) {
      tab.addEventListener('click', () => {
        const nextTab = tab.dataset.givebackTab;
        if (!nextTab || tab.classList.contains('giveback__tab--active')) return;

        this.#setActiveTab(nextTab);
      });
    }

    for (const trigger of this.querySelectorAll('[data-giveback-scroll-trigger]')) {
      trigger.addEventListener('click', () => {
        this.#showStartupForm(trigger);
      });
    }

    const startupForm = this.querySelector('#GivebackStartupForm');
    if (startupForm) {
      startupForm.addEventListener('submit', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('tab', 'startup');
        history.replaceState({ givebackTab: 'startup' }, '', url.toString());
      });
    }

    const initialTab = this.#getInitialTab();
    this.#setActiveTab(initialTab, false);

    if (this.querySelector('[data-giveback-form-result]')) {
      this.#showStartupForm(null, false);
    }

    const formResult = this.querySelector('[data-giveback-form-result]');
    if (formResult) {
      requestAnimationFrame(() => {
        formResult.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const resultHeading = formResult.querySelector('[tabindex="-1"]');
        if (resultHeading instanceof HTMLElement) {
          resultHeading.focus();
        }
      });
    }
  }

  #getInitialTab() {
    const url = new URL(window.location.href);
    const tabParam = url.searchParams.get('tab');

    if (tabParam === 'startup' || tabParam === 'community') {
      return tabParam;
    }

    if (this.querySelector('[data-giveback-form-result]')) {
      return 'startup';
    }

    if (this.#defaultTab === 'startup') return 'startup';
    if (this.#defaultTab === 'community') return 'community';
    return 'none';
  }

  #setActiveTab(tabName, updateUrl = true) {
    if (tabName === 'none') {
      for (const tab of this.#tabs) {
        tab.classList.remove('giveback__tab--active');
        tab.setAttribute('aria-selected', 'false');
        tab.tabIndex = -1;
      }

      if (this.#tabs[0]) {
        this.#tabs[0].tabIndex = 0;
      }

      for (const panel of this.#panels) {
        panel.classList.add('giveback__panel--hidden');
        panel.hidden = true;
      }

      if (!updateUrl) return;

      const url = new URL(window.location.href);
      url.searchParams.delete('tab');
      history.replaceState({ givebackTab: 'none' }, '', url.toString());
      return;
    }

    for (const tab of this.#tabs) {
      const isActive = tab.dataset.givebackTab === tabName;

      tab.classList.toggle('giveback__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
    }

    for (const panel of this.#panels) {
      const isActive = panel.dataset.givebackPanel === tabName;

      panel.classList.toggle('giveback__panel--hidden', !isActive);
      panel.hidden = !isActive;
    }

    if (tabName !== 'startup') {
      this.#hideStartupForm();
    }

    if (updateUrl && tabName !== 'none') {
      this.#scrollToPanel(tabName);
    }

    if (!updateUrl) return;

    const url = new URL(window.location.href);
    url.searchParams.set('tab', tabName);
    history.replaceState({ givebackTab: tabName }, '', url.toString());
  }

  #scrollToPanel(tabName) {
    const panel = this.#panels.find((entry) => entry.dataset.givebackPanel === tabName);
    if (!(panel instanceof HTMLElement)) return;

    const scrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';

    requestAnimationFrame(() => {
      panel.scrollIntoView({ behavior: scrollBehavior, block: 'start' });
    });
  }

  #showStartupForm(trigger, shouldScroll = true) {
    const formBlock = this.querySelector('[data-giveback-form-block]');
    if (!formBlock) return;

    const targetId = trigger?.closest('[data-giveback-scroll-target]')?.dataset.givebackScrollTarget;
    const target = (targetId && this.querySelector(`#${targetId}`)) || formBlock;

    formBlock.hidden = false;

    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-expanded', 'true');
    }

    if (!shouldScroll) return;

    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });

      const firstField = target.querySelector('input, textarea, select, button');
      if (firstField instanceof HTMLElement) {
        firstField.focus({ preventScroll: true });
      }
    });
  }

  #hideStartupForm() {
    if (this.querySelector('[data-giveback-form-result]')) return;

    const formBlock = this.querySelector('[data-giveback-form-block]');
    if (!formBlock) return;

    formBlock.hidden = true;

    for (const trigger of this.querySelectorAll('[data-giveback-scroll-trigger]')) {
      trigger.setAttribute('aria-expanded', 'false');
    }
  }
}

if (!customElements.get('giveback-component')) {
  customElements.define('giveback-component', GivebackComponent);
}
